import React, { useEffect, useMemo, useRef, useState } from "react";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import * as yup from "yup";
import dayjs from "dayjs";
import { fetchTeamMembersByTeamIds, fetchTeams } from "../../../services/api/teams";
import {
  createTaskComment,
  createTeamTask,
  deleteTask,
  deleteTaskAttachement,
  fetchTaskById,
  updateTaskComment,
  updateTeamTask,
} from "../../../services/api/teamTask";
import { showToast } from "../../../store/toast/toastSlice";
import BaseDatePicker from "../../../components/ui/BaseDatePicker";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import { fetchJobDetailsByReferenceId } from "../../../services/api/workOrder";
import { debounce } from "lodash";
import { C } from "@table-library/react-table-library/Cell-a4350b14";
import BaseDropdown from "../../../components/ui/BaseDropdown";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import { Avatar, Skeleton } from "@mui/material";
import { TeamTaskStatusType } from "../../../utils/enum";
import BaseEditor from "../../../components/ui/BaseEditor";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import BaseTooltip from "../../../components/ui/BaseTooltip";

import CommentLogsModal from "./CommentLogsModal";
import { useNavigate, useParams } from "react-router-dom";
import UserProfileLink from "../../../components/ui/UserProfileLink";
const validationSchema = yup.object({
  title: yup.string("Enter title").required("title is required"),
  description: yup.string("Enter description").notRequired(""),
  workOrderId: yup.number().notRequired(""),
  assigneeIds: yup
    .array()
    .of(yup.number()) // each element must be a number
    .notRequired(),
  teamId: yup.string().required("Team is required"),
  attachments: yup.array().of(
    yup.mixed().test("fileType", "Unknown file type", (file) => {
      if (!file) return true;

      // Skip existing files
      if (file.isExisting) return true;
      const allowedTypes = [
        // PDF
        "application/pdf",

        // Word
        "application/msword", // .doc
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx

        // Images
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",

        // Excel
        "application/vnd.ms-excel", // .xls
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
        "application/vnd.ms-excel.sheet.macroEnabled.12", // .xlsm
        "application/vnd.ms-excel.sheet.binary.macroEnabled.12", // .xlsb
        "application/vnd.ms-excel.template.macroEnabled.12", // .xltm
        "application/vnd.openxmlformats-officedocument.spreadsheetml.template", // .xltx

        // CSV
        "text/csv",
        "application/csv",
        "application/octet-stream", // sometimes CSV/Excel comes as this
      ];

      return allowedTypes.includes(file.type);
    }),
  ),
});
const priorityOptions = [
  { label: "Low", value: "LOW", color: "green" },
  { label: "Medium", value: "MEDIUM", color: "orange" },
  { label: "High", value: "HIGH", color: "red" },
];
const statusOptions = [
  { label: "To do", value: TeamTaskStatusType.TODO, color: "#FF5D20" },
  { label: "In-Progress", value: TeamTaskStatusType.INPROCESS, color: "#3C9FD4" },
  { label: "Completed", value: TeamTaskStatusType.COMPLETED, color: "#1FA52F" },
  { label: "On Hold", value: TeamTaskStatusType.ON_HOLD, color: "#9B59B6" },
];
const schema = {
  ...defaultSchema,
  tagNames: [...defaultSchema.tagNames, "u", "b", "strong", "i", "em"],
  attributes: {
    ...defaultSchema.attributes,
    a: ["href", "target", "rel"],
  },
};
const makeLinksClickable = (text) => {
  if (!text) return "";
  return text.replace(
    /(https?:\/\/[^\s<]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
  );
};

const autoLinkText = (text = "") => {
  if (!text) return null;

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[[rehypeRaw], [rehypeSanitize, schema]]}
      components={{
        a: ({ node, ...props }) => (
          <a
            {...props}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 underline break-words"
          />
        ),
        strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
        b: ({ node, ...props }) => <b className="font-semibold" {...props} />,
        em: ({ node, ...props }) => <em className="italic" {...props} />,
        u: ({ node, ...props }) => <u className="underline" {...props} />,
        ol: ({ node, ...props }) => (
          <ol className="list-decimal ml-5 text-sm space-y-1" {...props} />
        ),
        ul: ({ node, ...props }) => <ul className="list-disc ml-5 text-sm space-y-1" {...props} />,
        li: ({ node, ...props }) => <li className="break-words" {...props} />,
      }}
    >
      {makeLinksClickable(text)}
    </ReactMarkdown>
  );
};
const AddEditTaskModal = ({
  isAddEditTaskModal,
  setIsAddEditTaskModal,
  refetchAllTeamTask,
  setSelectedTask,
  selectedTask,
  authUserTeams,
  setTasksList,
  isCompanyOwner,
  authUser,
  workOrderDetail,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const { id: taskId } = useParams();
  const navigate = useNavigate();

  const {
    isFetching: isFetchTaskByIdApiLoading,
    error: isFetchTaskByIdApiError,
    data: taskData,
    refetch: refetchTaskById,
  } = useQuery({
    queryKey: ["taskData", selectedTask?.referenceId, taskId],
    queryFn: () => fetchTaskById(selectedTask?.referenceId || taskId),
    enabled: !workOrderDetail && (!!selectedTask?.referenceId || !!parseInt(taskId)),
  });
  const dispatch = useDispatch();
  const [workOrderInput, setWorkOrderInput] = useState(
    workOrderDetail
      ? `${workOrderDetail.referenceId} - ${workOrderDetail.title}`
      : taskData?.data?.workOrder?.referenceId || "",
  );
  const [workOrderSearch, setWorkOrderSearch] = useState(
    workOrderDetail?.referenceId || taskData?.data?.workOrder?.referenceId || "",
  );
  const [taskComment, setTaskComment] = useState(null);
  const [isTaskDropdownOpen, setIsTaskDropdownOpen] = useState(false);
  const [isAssigneeSelectFieldOpen, setisAssigneeSelectFieldOpen] = useState(false);
  const fileInputRef = useRef(null);
  const isEditModal = !!taskData?.data;
  const isCreatedBy = authUser?.id === taskData?.data?.createdBy?.id;
  const [showEditor, setShowEditor] = useState(false);

  const { data: workOrderData, isFetching: isWorkOrderLoading } = useQuery({
    queryKey: ["workOrders", workOrderSearch],
    queryFn: () => fetchJobDetailsByReferenceId(workOrderSearch),
    enabled: workOrderSearch?.length >= 4,
  });

  const {
    isPending: isCreateTeamTaskApiLoading,
    error: isCreateTeamTaskApiError,
    mutateAsync: createTeamTaskMutation,
  } = useMutation({
    mutationFn: createTeamTask,
    enabled: false,
  });

  const {
    data: teamsData,
    isFetching: isTeamsLoading,
    error: teamsError,
    refetch: refetchTeams,
  } = useQuery({
    queryKey: ["teamsData"],
    queryFn: () => fetchTeams(),
  });

  const {
    isPending: isUpdateTeamTaskApiLoading,
    error: isUpdateTeamTaskApiError,
    data: updatedTask,
    mutateAsync: updateTeamTaskMutation,
  } = useMutation({
    mutationFn: updateTeamTask,
    enabled: false,
  });

  const {
    isPending: isDeleteTaskApiLoading,
    error: isDeleteTaskApiError,
    mutateAsync: deleteTaskMutation,
  } = useMutation({
    mutationFn: deleteTask,
    enabled: false,
  });

  const {
    isPending: isCreateTaskCommentApiLoading,
    error: isCreateTaskCommentApiError,
    // data: ,
    mutateAsync: createTaskCommentMutation,
  } = useMutation({
    mutationFn: createTaskComment,
    enabled: false,
  });

  const {
    isPending: isDeleteTaskAttachementApiLoading,
    error: isDeleteTaskAttachementApiError,
    mutateAsync: deleteTaskAttachementMutation,
  } = useMutation({
    mutationFn: deleteTaskAttachement,
    enabled: false,
  });

  const formik = useFormik({
    initialValues: {
      title: taskData?.data?.title || null,
      description: taskData?.data?.description || null,
      startDate: taskData?.data?.startDate || null,
      dueDate: taskData?.data?.dueDate || null,
      workOrderId: taskData?.data?.workOrder?.id || workOrderDetail?.id || null,
      assigneeIds: taskData?.data?.assignees?.map((member) => member.id) || [],
      teamId: taskData?.data?.team.id || null,
      priority: taskData?.data?.priority || null,
      status: taskData?.data?.status || null,
      attachments:
        taskData?.data?.attachments?.map((file) => ({
          id: file?.id, // if exists
          url: file?.url,
          name: file?.fileName,
          isExisting: true,
        })) ||
        [] ||
        [],
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {},
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });
  const {
    isFetching: isfetchTeamMembersByTeamIdApiLoading,
    refetch: refetchTeamMembersByTeamId,
    data: teamMembersData,
  } = useQuery({
    queryKey: ["teamMembers", formik.values.teamId],
    queryFn: () => fetchTeamMembersByTeamIds(formik.values.teamId),
    enabled: !!isAddEditTaskModal && !!formik.values.teamId,
  });

  const formattedWorkOrder = useMemo(() => {
    if (workOrderData?.data) {
      return [
        {
          label: `${workOrderData.data.referenceId} - ${workOrderData.data.title}`,
          value: workOrderData.data.id,
        },
      ];
    }
    if (workOrderDetail) {
      return [
        {
          label: `${workOrderDetail.referenceId} - ${workOrderDetail.title}`,
          value: workOrderDetail.id,
        },
      ];
    }
    return [];
  }, [workOrderData, workOrderDetail]);

  const formattedTeamMembers = teamMembersData?.data?.map((member) => {
    return {
      label: `${member.firstName} ${member.lastName}`,
      value: member.id,
      profilePicture: member.profilePicture,
      teamId: formik.values?.teamId,
    };
  });

  const teamIds = authUserTeams?.map((team) => team.id) || [];

  const formattedTeamsData = teamsData?.data?.data
    ?.filter((team) => {
      if (isCompanyOwner) return true;
      return teamIds.includes(team.id);
    })
    .map((team) => ({
      label: team.name,
      value: team.id,
    }));

  const debouncedSetWorkOrderSearch = useMemo(
    () =>
      debounce((value) => {
        setWorkOrderSearch(value);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetWorkOrderSearch.cancel();
    };
  }, [debouncedSetWorkOrderSearch]);

  useEffect(() => {
    if (!workOrderInput) {
      formik.setFieldValue("workOrderId", null);
    }
  }, [workOrderInput]);

  const handleCreateUpdateTask = async () => {
    if (isEditModal) {
      await handleUpdateTask();
    } else {
      await handleCreateNewTask();
    }
  };

  const handleCreateNewTask = async () => {
    try {
      const formData = new FormData();

      // Append normal fields
      formData.append("title", formik.values.title || "");
      formData.append("description", formik.values.description || "");
      if (formik.values.startDate) formData.append("startDate", formik.values.startDate);
      if (formik.values.dueDate) formData.append("dueDate", formik.values.dueDate);
      if (formik.values.workOrderId) formData.append("workOrderId", formik.values.workOrderId);
      if (formik.values.teamId) formData.append("teamId", formik.values.teamId);
      if (formik.values.priority) formData.append("priority", formik.values.priority);
      if (formik.values.status) formData.append("status", formik.values.status);

      // Append array values (assignedToIds)
      formik.values.assigneeIds.forEach((id) => formData.append("assigneeIds[]", id));

      // Append files
      (formik.values.attachments || []).forEach((file) => formData.append("attachments", file));

      const createdTask = await createTeamTaskMutation(formData);

      // resetForm();
      dispatch(
        showToast({
          type: "success",
          message: "Task created successfully",
        }),
      );
      setIsAddEditTaskModal(false);
      setTasksList?.((prev) => [createdTask.data, ...prev]);
    } catch (error) {
      dispatch(
        showToast({
          type: "error",
          message: error.response.data.message,
        }),
      );
    }
  };

  const handleUpdateTask = async () => {
    try {
      const formData = new FormData();

      // Append normal fields
      formData.append("id", selectedTask.id);
      formData.append("title", formik.values.title || "");
      formData.append("description", formik.values.description || "");
      if (formik.values.startDate) formData.append("startDate", formik.values.startDate);
      if (formik.values.dueDate) formData.append("dueDate", formik.values.dueDate);
      if (formik.values.workOrderId) formData.append("workOrderId", formik.values.workOrderId);
      if (formik.values.teamId) formData.append("teamId", formik.values.teamId);
      if (formik.values.priority) formData.append("priority", formik.values.priority);
      if (formik.values.status) formData.append("status", formik.values.status);

      // Filter assignees whose team matches the selected team
      const validAssigneeIds = formik.values.assigneeIds.filter((assigneeId) => {
        const member = formattedTeamMembers.find((m) => m.value === assigneeId);
        return member?.teamId === formik.values.teamId; // only include if team matches
      });

      // Append to FormData
      validAssigneeIds.forEach((id) => formData.append("assigneeIds[]", id));

      const existingFiles = formik.values.attachments.filter((f) => f.isExisting);
      const newFiles = formik.values.attachments.filter((f) => !f.isExisting);
      // Append files
      newFiles.forEach((file) => {
        formData.append("attachments", file);
      });

      const updatedTask = await updateTeamTaskMutation(formData);

      setTasksList((prev) =>
        prev.map((task) => (task.id === taskData?.data.id ? updatedTask.data : task)),
      );

      // resetForm();
      dispatch(
        showToast({
          type: "success",
          message: "Task updated successfully",
        }),
      );
      setIsAddEditTaskModal(false);
    } catch (error) {
      /* dispatch(
        showToast({
          type: "error",
          message: error.response.data.message,
        }),
      ); */
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const selectedMembers = formattedTeamMembers?.filter((member) =>
    formik.values.assigneeIds?.includes(member.value),
  );
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    if (!files.length) return;

    formik.setFieldValue("attachments", [...formik.values.attachments, ...files]);

    e.target.value = ""; // allow re-selecting same file
  };

  const handleTaskComment = async () => {
    try {
      if (!taskComment) return;

      const cleanedComment = taskComment.replace(/(<p><br><\/p>)+$/g, "").trim();

      if (!cleanedComment) return;

      const payload = {
        id: taskData?.data?.id,
        comment: cleanedComment,
      };
      const newComment = await createTaskCommentMutation(payload);

      queryClient.setQueryData(["taskData", taskData?.data.id], (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          data: {
            ...oldData.data,
            comments: [newComment.data, ...(oldData.data.comments || [])],
          },
        };
      });

      setTaskComment("");
    } catch (error) {
      error.response.data.message?.map((err) =>
        dispatch(
          showToast({
            type: "error",
            message: err,
          }),
        ),
      );
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (file) => {
    const type = file.type;

    // PDF
    if (type === "application/pdf") return "fa-file-pdf text-red-500";

    // Images
    if (
      type === "image/jpeg" ||
      type === "image/jpg" ||
      type === "image/png" ||
      type === "image/webp"
    )
      return "fa-file-image text-green-500";

    // Word (.doc, .docx)
    if (
      type === "application/msword" ||
      type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    )
      return "fa-file-word text-blue-600";

    // Excel (.xls, .xlsx, .xlsm, .xlsb, templates)
    if (
      type === "application/vnd.ms-excel" ||
      type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      type === "application/vnd.ms-excel.sheet.macroEnabled.12" ||
      type === "application/vnd.ms-excel.sheet.binary.macroEnabled.12" ||
      type === "application/vnd.ms-excel.template.macroEnabled.12" ||
      type === "application/vnd.openxmlformats-officedocument.spreadsheetml.template"
    )
      return "fa-file-excel text-emerald-600";

    // CSV
    if (type === "text/csv" || type === "application/csv") return "fa-file-csv text-green-600";

    return "fa-file text-gray-500"; // default icon
  };

  const handleDeleteTask = async () => {
    try {
      const deletedTask = await deleteTaskMutation(taskData?.data?.id);

      setTasksList((prev) => prev.filter((task) => task.id !== taskData?.data?.id));

      dispatch(
        showToast({
          type: "success",
          message: "Task deleted successfully",
        }),
      );

      setIsAddEditTaskModal(false);
      setSelectedTask(null);
    } catch (error) {
      dispatch(
        showToast({
          type: "error",
          message: error.response.data.message,
        }),
      );
    }
  };
  const isEmptyComment = (html) => {
    if (!html) return true;
    const text = html.replace(/<[^>]*>/g, "").trim();

    return text.length === 0;
  };
  return (
    <BaseModal
      open={isAddEditTaskModal}
      close={() => {
        onClose ? onClose() : navigate("/taskBoard");
        setSelectedTask?.(null);
        setIsAddEditTaskModal(false);
      }}
    >
      {isFetchTaskByIdApiLoading || isfetchTeamMembersByTeamIdApiLoading ? (
        <AddEditTaskModalSkeleton />
      ) : (
        <div className="w-[700px] relative">
          <div
            className="flex justify-between items-center bg-white w-full  sticky top-0  z-10 px-6 py-4"
            style={{
              boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
            }}
          >
            <h6 className="text-dark font-normal break-words max-w-[90%]">
              {isEditModal
                ? `#${taskData?.data?.referenceId} - ${taskData?.data?.title}`
                : "Add Task"}
            </h6>
            {isCreatedBy && (
              <div className="relative">
                <i
                  className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsTaskDropdownOpen(!isTaskDropdownOpen);
                  }}
                ></i>
                <BaseDropdown
                  setOpen={setIsTaskDropdownOpen}
                  open={isTaskDropdownOpen}
                  className="w-[236px] top-[30px] shadow-none left-[-210px] "
                  style={{
                    boxShadow: "0 1px 10px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  <div
                    className="hover:bg-[#F6F6F6]	 py-3 px-1 cursor-pointer rounded-t-lg"
                    onClick={handleDeleteTask}
                  >
                    <p className="capitalize font-normal">Delete</p>
                  </div>
                </BaseDropdown>
              </div>
            )}
          </div>
          <form>
            <div className="px-6 pt-4 grid grid-cols-3 gap-x-4 gap-y-2 pb-4  overflow-y-auto">
              <div className="col-span-3">
                <div className="flex items-center relative ">
                  <i
                    className="fa-light fa-user-circle-plus text-primary text-xl cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setisAssigneeSelectFieldOpen(!isAssigneeSelectFieldOpen);
                    }}
                  ></i>
                  <div className="flex items-center flex-wrap gap-x-4 gap-y-2 w-full ml-4 ">
                    {selectedMembers?.map((member) => (
                      <UserProfileLink
                        as="div"
                        key={member?.value}
                        userId={member?.value}
                        className="flex items-center"
                        linkClassName="cursor-pointer group"
                      >
                        <Avatar
                          alt={member?.label}
                          src={member?.profilePicture}
                          sx={{ width: 34, height: 32 }}
                        />
                        <p className="ml-2 text-sm text-dark group-hover:text-primary group-hover:underline">
                          {member?.label}
                        </p>
                      </UserProfileLink>
                    ))}
                  </div>
                  <BaseDropdown
                    setOpen={setisAssigneeSelectFieldOpen}
                    open={isAssigneeSelectFieldOpen}
                    className="w-[266px] top-[35px] shadow-none left-[0px] "
                    style={{
                      boxShadow: "0 1px 10px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    {formattedTeamMembers?.map((member) => {
                      const isSelected = formik.values.assigneeIds?.includes(member.value);

                      return (
                        <div
                          className="hover:bg-[#F6F6F6] flex items-center py-3 px-1 cursor-pointer border-b"
                          onClick={() => {
                            let updatedIds = [...(formik.values.assigneeIds || [])];

                            if (isSelected) {
                              // remove
                              updatedIds = updatedIds.filter((id) => id !== member.value);
                            } else {
                              // add
                              updatedIds.push(member.value);
                            }

                            formik.setFieldValue("assigneeIds", updatedIds);
                          }}
                        >
                          <BaseCheckbox color="orange" checked={isSelected} />
                          <div className="flex items-center ml-4">
                            <Avatar
                              alt={member?.label}
                              src={member?.profilePicture}
                              sx={{ width: 32, height: 32 }}
                            />
                            <p className="ml-2">{member?.label}</p>
                          </div>
                        </div>
                      );
                    })}
                  </BaseDropdown>
                </div>
              </div>

              <BaseAutoSelect
                label="Status"
                labelShrink={false}
                required={true}
                placeholder="Select Status"
                fullHeight={false}
                height="0"
                options={statusOptions}
                id="status"
                name="status"
                value={formik.values?.status}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.status && formik.errors.status}
                helperText={formik.touched.status && formik.errors.status}
                renderOption={(props, option) => (
                  <p {...props} style={{ color: option.color }}>
                    {option.label}
                  </p>
                )}
              />

              <BaseAutoSelect
                label="Priority"
                labelShrink={false}
                required={true}
                placeholder="Select Priority"
                fullHeight={false}
                height="0"
                options={priorityOptions}
                id="priority"
                name="priority"
                value={formik.values?.priority}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.priority && formik.errors.priority}
                helperText={formik.touched.priority && formik.errors.priority}
                renderOption={(props, option) => (
                  <p {...props} style={{ color: option.color }}>
                    {option.label}
                  </p>
                )}
              />
              <div>
                <p className="text-xs font-normal">Start Date</p>
                <BaseDatePicker
                  disabled={isEditModal ? !isCreatedBy : false}
                  className="border-none w-full"
                  type="date"
                  id="startDate"
                  name="startDate"
                  maxDate={formik.values.dueDate ? dayjs(formik.values.dueDate) : null}
                  value={formik.values.startDate ? dayjs(formik.values.startDate) : null}
                  onChange={(event) => {
                    const selectedDate = dayjs(event)
                      .utcOffset(0, true)
                      .format("MM-DD-YYYY hh:mm:ss");
                    formik.setFieldValue("startDate", selectedDate);
                  }}
                  isCustomStyle={true}
                />
              </div>
              <div>
                <p className="text-xs font-normal">Due Date</p>
                <BaseDatePicker
                  disabled={isEditModal ? !isCreatedBy : false}
                  className="border-none w-full"
                  type="date"
                  id="dueDate"
                  name="dueDate"
                  value={formik.values.dueDate ? dayjs(formik.values.dueDate) : null}
                  minDate={formik.values.startDate ? dayjs(formik.values.startDate) : null}
                  onChange={(event) => {
                    const selectedDate = dayjs(event)
                      .utcOffset(0, true)
                      .format("MM-DD-YYYY hh:mm:ss");
                    formik.setFieldValue("dueDate", selectedDate);
                  }}
                  isCustomStyle={true}
                />
              </div>

              <BaseAutoSelect
                className="mb-2"
                labelShrink={false}
                required={true}
                label="Select Work Order"
                placeholder="Link Work Order"
                fullHeight={false}
                height="0"
                options={formattedWorkOrder || []}
                id="workOrderId"
                name="workOrderId"
                filterOptions={(x) => x}
                inputValue={workOrderInput}
                value={
                  formattedWorkOrder.find((opt) => opt.value === formik.values.workOrderId) || null
                }
                onInputChange={(_, value, reason) => {
                  if (reason === "input") {
                    setWorkOrderInput(value);
                    debouncedSetWorkOrderSearch(value);
                  }
                }}
                onChange={(e) => {
                  formik.setFieldValue("workOrderId", e.target.value || null);
                  setWorkOrderInput(e.target.label || "");
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.workOrderId && formik.errors.workOrderId}
                helperText={formik.touched.workOrderId && formik.errors.workOrderId}
              />
              <BaseAutoSelect
                label="Teams"
                labelShrink={false}
                required={true}
                placeholder="Select Team"
                fullHeight={false}
                height="0"
                options={formattedTeamsData}
                id="teamId"
                name="teamId"
                value={formik.values?.teamId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.teamId && formik.errors.teamId}
                helperText={formik.touched.teamId && formik.errors.teamId}
                renderOption={(props, option) => (
                  <p {...props} style={{ color: option.color }}>
                    {option.label}
                  </p>
                )}
              />

              <div className="col-span-3">
                <BaseTextField
                  disabled={isEditModal ? !isCreatedBy : false}
                  label="Title"
                  labelShrink={false}
                  placeholder="Enter Title"
                  optional={false}
                  isDefaultField={false}
                  fullHeight={false}
                  height="10px"
                  className="mb-3"
                  id="title"
                  name="title"
                  value={formik.values?.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.title && formik.errors.title}
                  helperText={formik.touched.title && formik.errors.title}
                />

                <div className="col-span-3">
                  <p className="text-sm font-normal mb-2">Description</p>

                  <BaseEditor
                    readOnly={isEditModal ? !isCreatedBy : false}
                    className={`
                    w-full 
                    text-sm text-gray-800 
                    px-3 py-2 
                    resize-none 
                    overflow-y-auto 
                    focus:outline-none 
                    focus:ring-1 
                    focus:ring-orange-400 
                    border border-gray-200 
                    rounded-md 
                    max-h-[200px]
                    ${isEditModal && !isCreatedBy ? "!bg-[#F6F6F6] !cursor-not-allowed !text-gray-500" : "bg-white"}
                  `}
                    editorClassName="ql-editor"
                    value={formik.values.description || ""}
                    placeholder="Description"
                    showToolbar={true}
                    onKeyDown={formik.handleChange}
                    onChange={(body) => {
                      if (!body) {
                        formik.setFieldValue("description", "");
                        return;
                      }

                      let cleanHTML = body;
                      const tempContainer = document.createElement("div");
                      tempContainer.innerHTML = body;
                      const children = tempContainer.children;
                      const lastChild = children[children.length - 1];

                      const isTempListItem =
                        lastChild &&
                        (lastChild.tagName === "LI" || lastChild.tagName === "P") &&
                        lastChild.innerHTML === "<br>";

                      if (!isTempListItem) {
                        cleanHTML = body.replace(/(<p><br><\/p>)+$/g, "");
                      }

                      formik.setFieldValue("description", cleanHTML);
                    }}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.description && formik.errors.description && (
                    <p className="text-red-500 text-xs mt-1">{formik.errors.description}</p>
                  )}
                </div>

                <div className="my-2 flex flex-wrap gap-2">
                  {formik.values.attachments?.map((file, index) => (
                    <div
                      key={index}
                      className="flex justify-between bg-zinc-100 rounded px-1 py-2 border"
                    >
                      <div className="flex items-center">
                        <i className={`fa-solid ${getFileIcon(file)} text-3xl`}></i>

                        <div className="ml-2">
                          {file?.isExisting ? (
                            // Existing files: clickable link
                            <a
                              href={file.url} // make sure this is the full URL to the file
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-dark font-normal truncate hover:!text-primary hover:underline hover:font-normal"
                            >
                              {file.name}
                            </a>
                          ) : (
                            // New files: just show name
                            <p className="text-sm text-dark font-normal truncate">{file.name}</p>
                          )}

                          {!file?.isExisting && (
                            <p className="text-xs truncate">{formatFileSize(file.size)}</p>
                          )}
                        </div>
                      </div>

                      <i
                        className="fa-regular fa-trash-can text-red-500 cursor-pointer ml-6"
                        onClick={async () => {
                          const newFiles = formik.values.attachments.filter((_, i) => i !== index);
                          formik.setFieldValue("attachments", newFiles);
                          if (isEditModal && file?.isExisting) {
                            await deleteTaskAttachementMutation(file?.id);
                            queryClient.setQueryData(["taskData", selectedTask?.id], (oldData) => {
                              if (!oldData) return oldData;

                              return {
                                ...oldData,
                                data: {
                                  ...oldData.data,
                                  attachments: oldData.data.attachments.filter(
                                    (att) => att.id !== file.id,
                                  ),
                                },
                              };
                            });
                          }
                        }}
                      ></i>
                    </div>
                  ))}
                </div>

                <div className="w-full flex items-center mt-6">
                  {/* Hidden File Input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    multiple
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />

                  {/* Choose File Button */}
                  <BaseButton
                    loaderSize={22}
                    // isLoading={isUploadingProfilePictureLoading}
                    variant="outlined"
                    color="primary"
                    className="py-[2px] border-gray-400 text-gray-600"
                    onClick={handleButtonClick}
                  >
                    Add Attachment
                  </BaseButton>
                </div>
              </div>

              {isEditModal && (
                <section className="mt-2 col-span-3" id="commentSection">
                  <p className="text-dark font-normal">Comments</p>

                  <div className="flex flex-col mt-2 w-full">
                    <BaseEditor
                      className="
                      w-full 
                      text-sm text-gray-800 
                      px-3 py-2 
                      resize-none 
                      overflow-y-auto 
                      focus:outline-none 
                      focus:ring-1 
                      focus:ring-orange-400 
                      border border-gray-200 
                      rounded-md 
                       max-h-[200px]
                    "
                      value={taskComment}
                      placeholder="Add a comment..."
                      showToolbar={showEditor}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          e.stopPropagation();

                          if (!isEmptyComment(taskComment)) {
                            handleTaskComment();
                          }
                        }
                      }}
                      onChange={(body) => {
                        if (!body) {
                          setTaskComment("");
                          return;
                        }

                        let cleanHTML = body;
                        const tempContainer = document.createElement("div");
                        tempContainer.innerHTML = body;
                        const children = tempContainer.children;
                        const lastChild = children[children.length - 1];

                        const isTempListItem =
                          lastChild &&
                          (lastChild.tagName === "LI" || lastChild.tagName === "P") &&
                          lastChild.innerHTML === "<br>";

                        if (!isTempListItem) {
                          cleanHTML = body.replace(/(<p><br><\/p>)+$/g, "");
                        }

                        setTaskComment(cleanHTML);
                      }}
                    />
                    <div className="border-t bg-gray-100 flex items-center justify-end px-4  space-x-5 flex justify-end mb-1">
                      <BaseTooltip content="Show Formatting Options">
                        <i
                          className={`fa-light fa-pencil text-lg cursor-pointer hover:text-primary transition ${showEditor ? "text-primary" : "text-gray-500"}`}
                          onClick={() => setShowEditor((prev) => !prev)}
                        ></i>
                      </BaseTooltip>
                    </div>

                    <BaseButton
                      disabled={
                        !taskComment ||
                        taskComment.length === 0 ||
                        isCreateTaskCommentApiLoading ||
                        isEmptyComment(taskComment)
                      }
                      className="py-[3px] px-[24px] bg-primary mt-3 self-end"
                      onClick={handleTaskComment}
                    >
                      Send
                    </BaseButton>
                  </div>
                  <div className="mb-4">
                    {taskData?.data?.comments.length > 0 ? (
                      taskData?.data?.comments?.map((comment) => (
                        <Comment
                          key={comment?.id}
                          comment={comment}
                          isCreatedBy={authUser?.id === comment?.createdBy?.id}
                          refetchTaskById={refetchTaskById}
                        />
                      ))
                    ) : (
                      <div>
                        <h6 className="text-center font-normal">No comments yet</h6>
                        <p className="text-center">Be the first to comment</p>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {taskData?.data?.logs?.length > 0 && (
                <section className="mt-2 col-span-3">
                  <TaskActivityLog activityLogs={taskData?.data?.logs} />
                </section>
              )}
            </div>
          </form>
          <div
            className="d-flex content-center py-6 sticky bottom-0 bg-white w-full z-10"
            style={{
              boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
            }}
          >
            <BaseButton
              className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
              variant="outlined"
              color="primary"
              style={{
                marginRight: "16px",
              }}
              onClick={() => {
                onClose?.();
                setIsAddEditTaskModal(false);
                setSelectedTask?.(false);
              }}
            >
              Cancel
            </BaseButton>

            <BaseButton
              isLoading={isCreateTeamTaskApiLoading || isUpdateTeamTaskApiLoading}
              loaderSize={24}
              loaderColor="primary"
              className="px-[12px] py-[5px] min-w-32"
              color="primary"
              disabled={!formik.isValid}
              onClick={handleCreateUpdateTask}
            >
              {isEditModal ? "Update" : "Add"}
            </BaseButton>
          </div>
        </div>
      )}
    </BaseModal>
  );
};

export default AddEditTaskModal;

const Comment = ({ comment, isCreatedBy, refetchTaskById }) => {
  const formattedText = autoLinkText(comment?.comment || "");

  const [isCommentActionsDropdownOpen, setIsCommentActionsDropdownOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedComment, setEditedComment] = useState(comment?.comment);

  const [isCommentLogsModalOpen, setIsCommentLogsModalOpen] = useState(false);

  const {
    isPending: isUpdateTaskCommentApiLoading,
    error: isUpdateTaskCommentApiError,
    // data: ,
    mutateAsync: updateTaskCommentMutation,
  } = useMutation({
    mutationFn: updateTaskComment,
    enabled: false,
  });

  const handleEditClick = () => {
    setIsCommentActionsDropdownOpen(false);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedComment(comment?.comment);
    setIsEditing(false);
  };

  const handleUpdateComment = async (commentId) => {
    const payload = {
      id: commentId,
      comment: editedComment,
    };
    try {
      const updatedComment = await updateTaskCommentMutation(payload);
      await refetchTaskById();

      setEditedComment(updatedComment?.data?.comment);
    } catch (error) {}
    setIsEditing(false);
  };
  return (
    <div className="flex mb-6 relative mt-4">
      <UserProfileLink
        as="div"
        userId={comment?.createdBy?.id}
        linkClassName="cursor-pointer"
      >
        <Avatar
          src={
            comment?.createdBy?.digitalAsset?.length > 0
              ? comment?.createdBy?.digitalAsset[0]?.url
              : null
          }
          sx={{ width: 34, height: 34 }}
        />
      </UserProfileLink>
      <div className="w-full ml-3">
        {!isEditing && (
          <div>
            <div className="flex items-center group relative">
              <div className="flex justify-end flex-col bg-light rounded-xl px-3 py-1.5">
                <div className="flex items-center">
                  <UserProfileLink
                    userId={comment?.createdBy?.id}
                    className="text-primary font-medium"
                    linkClassName="cursor-pointer hover:underline"
                  >
                    {comment?.createdBy?.firstName + " " + comment?.createdBy?.lastName}
                  </UserProfileLink>
                  <p className="text-zinc-400 text-[10px] ml-2 mb-0.5">
                    {dayjs(comment?.createdAt).fromNow()}
                  </p>
                </div>
                <p className="mr-12"> {autoLinkText(comment?.comment || "")}</p>
              </div>

              {isCreatedBy && (
                <div className="flex items-center relative opacity-0 group-hover:opacity-100 transition-opacity duration-200 ml-4">
                  <i
                    className="fa-solid fa-ellipsis cursor-pointer text-base"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsCommentActionsDropdownOpen(!isCommentActionsDropdownOpen);
                    }}
                  ></i>

                  {/* Dropdown positioned below icon with your current styling */}
                  <BaseDropdown
                    setOpen={setIsCommentActionsDropdownOpen}
                    open={isCommentActionsDropdownOpen}
                    className="w-[165px] top-[24px] shadow-none left-[-140px]"
                    style={{
                      boxShadow: "0 1px 10px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    <div
                      className="hover:bg-[#F6F6F6] py-2 px-1 cursor-pointer rounded-lg flex items-center"
                      onClick={handleEditClick}
                    >
                      <i className="fa-light fa-pencil cursor-pointer text-gray-900 text-xs"></i>
                      <p className="capitalize ml-2">Edit</p>
                    </div>
                  </BaseDropdown>
                </div>
              )}
            </div>
            <div className="flex">
              {isUpdateTaskCommentApiLoading ? (
                <p className="text-xs  px-2 mt-1">Posting...</p>
              ) : (
                comment?.logs?.length > 0 && (
                  <p
                    className="text-xs font-normal px-2 mt-1 cursor-pointer hover:underline underline-offset-2"
                    onClick={() => setIsCommentLogsModalOpen(true)}
                  >
                    Edited
                  </p>
                )
              )}
              {isCommentLogsModalOpen && (
                <CommentLogsModal
                  open={isCommentLogsModalOpen}
                  close={setIsCommentLogsModalOpen}
                  logs={comment?.logs}
                />
              )}
            </div>
          </div>
        )}
        {isEditing && (
          <div>
            <BaseEditor
              value={editedComment}
              showToolbar={true}
              className="bg-white border border-gray-100 w-full rounded-md mt-1
  focus:outline-none focus:ring-1 focus:ring-orange-400 text-xs font-[300] shadow-md"
              onChange={(body) => {
                if (!body) {
                  setEditedComment("");
                  return;
                }

                let cleanHTML = body;
                const tempContainer = document.createElement("div");
                tempContainer.innerHTML = body;
                const children = tempContainer.children;
                const lastChild = children[children.length - 1];

                const isTempListItem =
                  lastChild &&
                  (lastChild.tagName === "LI" || lastChild.tagName === "P") &&
                  lastChild.innerHTML === "<br>";

                if (!isTempListItem) {
                  cleanHTML = body.replace(/(<p><br><\/p>)+$/g, "");
                }

                setEditedComment(cleanHTML);
              }}
            />
            <div className="flex justify-between mt-1">
              <div className="flex">
                {isUpdateTaskCommentApiLoading ? (
                  <p className="text-xs  px-2">Posting...</p>
                ) : (
                  comment?.logs?.length > 0 && (
                    <p
                      className="text-xs font-normal px-2 mt-1 cursor-pointer hover:underline underline-offset-2"
                      onClick={() => setIsCommentLogsModalOpen(true)}
                    >
                      Edited
                    </p>
                  )
                )}
                {isCommentLogsModalOpen && (
                  <CommentLogsModal
                    open={isCommentLogsModalOpen}
                    close={setIsCommentLogsModalOpen}
                    logs={comment?.logs}
                  />
                )}
              </div>
              <div>
                <BaseButton
                  onClick={handleCancel}
                  className="px-[2px] py-[4px] text-dark text-xs bg-gray-200 "
                >
                  Cancel
                </BaseButton>
                <BaseButton
                  isLoading={isUpdateTaskCommentApiLoading}
                  color="primary"
                  onClick={() => handleUpdateComment(comment?.id)}
                  className="px-[2px] py-[4px] text-xs ml-2"
                >
                  Save
                </BaseButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AddEditTaskModalSkeleton = () => {
  return (
    <div className="w-[700px] p-6 animate-pulse">
      {/* Header */}
      <Skeleton variant="text" width={200} height={28} className="mb-6" />

      {/* Assignees */}
      <div className="flex items-center mb-4">
        <Skeleton variant="circular" width={34} height={32} />
        <Skeleton variant="circular" width={34} height={32} className="ml-4" />
        <Skeleton variant="circular" width={34} height={32} className="ml-4" />
        <Skeleton variant="rectangular" width={100} height={32} className="ml-auto" />
      </div>

      {/* Status, Priority, Start Date, Due Date */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        <Skeleton variant="rectangular" height={40} />
        <Skeleton variant="rectangular" height={40} />
        <Skeleton variant="rectangular" height={40} />
        <Skeleton variant="rectangular" height={40} />
      </div>

      {/* Work Order + Teams */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <Skeleton variant="rectangular" height={40} />
        <Skeleton variant="rectangular" height={40} />
      </div>

      {/* Title */}
      <Skeleton variant="rectangular" height={40} className="mb-3" />

      {/* Description */}
      <Skeleton variant="rectangular" height={176} className="mb-3" />

      {/* Attachments */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Skeleton variant="rectangular" width={100} height={40} />
        <Skeleton variant="rectangular" width={100} height={40} />
        <Skeleton variant="rectangular" width={100} height={40} />
      </div>

      {/* Comment Section */}
      <Skeleton variant="text" width={120} height={20} className="mb-3" />
      <div className="mb-3">
        <div className="flex mb-3 items-center">
          <Avatar sx={{ width: 34, height: 34 }} />
          <Skeleton variant="text" width={200} height={20} className="ml-3" />
        </div>
        <Skeleton variant="rectangular" height={80} className="mb-2" />
        <Skeleton variant="rectangular" height={40} width={120} className="ml-auto" />
      </div>

      {/* Footer Buttons */}
      <div className="flex justify-end gap-4 mt-6">
        <Skeleton variant="rectangular" width={100} height={36} />
        <Skeleton variant="rectangular" width={120} height={36} />
      </div>
    </div>
  );
};

const TaskActivityLog = ({ activityLogs }) => {
  const filteredLogs = activityLogs?.filter((log) =>
    ["TASK_CREATED", "ASSIGNED", "UNASSIGNED", "STATUS_CHANGED"].includes(log.action),
  );

  const userLink = (user) => (
    <UserProfileLink as="span" userId={user?.id} linkClassName="cursor-pointer hover:underline">
      {`${user?.firstName} ${user?.lastName}`}
    </UserProfileLink>
  );

  // "A, B and C", with every name a link into that person's profile.
  const formatNames = (users = []) =>
    users?.map((user, index) => (
      <span key={user?.id ?? index}>
        {index > 0 && (index === users.length - 1 ? " and " : ", ")}
        {userLink(user)}
      </span>
    ));

  const renderActionText = (log) => {
    const performer = userLink(log?.performedBy);

    switch (log.action) {
      case "TASK_CREATED":
        return (
          <>
            <span>Task created</span>
            <span className="ml-1">by</span>
            <span className="ml-1 font-normal">{performer}</span>
          </>
        );

      case "ASSIGNED":
        return (
          <>
            <span>Task assigned</span>
            {log?.meta?.to?.length > 0 && (
              <>
                <span className="ml-1">to</span>
                <span className="ml-1 font-normal">{formatNames(log.meta.to)}</span>
              </>
            )}
            <span className="ml-1">by</span>
            <span className="ml-1 font-normal">{performer}</span>
          </>
        );

      case "UNASSIGNED":
        return (
          <>
            <span>Task unassigned</span>
            {log?.meta?.to?.length > 0 && (
              <>
                <span className="ml-1">from</span>
                <span className="ml-1 font-normal">{formatNames(log.meta.to)}</span>
              </>
            )}
            <span className="ml-1">by</span>
            <span className="ml-1 font-normal">{performer}</span>
          </>
        );

      case "STATUS_CHANGED":
        const statusLabels = {
          TODO: "To Do",
          INPROCESS: "In-Progress",
          COMPLETED: "Completed",
          ON_HOLD: "On Hold",
        };
        return (
          <>
            <span>Status changed</span>
            <span className="ml-1 font-normal">
              {statusLabels[log?.meta?.from]} → {statusLabels[log?.meta?.to]}
            </span>
            <span className="ml-1">by</span>
            <span className="ml-1 font-normal">{performer}</span>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col items-center justify-center cursor-default">
      <div className="border-b w-72 mb-5"></div>

      {filteredLogs?.map((log) => (
        <div key={log.id} className="flex items-center mb-2">
          <p className="text-xs text-center">{renderActionText(log)}</p>
          <p className="text-[10px] ml-1"> - {dayjs(log?.createdAt).fromNow()}</p>
        </div>
      ))}
    </div>
  );
};
